// src/Admin/emailDesign.js
/* =========================================================
   The RoboMind email design used by the Send Emails page.
   You write plain text; this turns it into a branded email that looks
   right in Gmail, Outlook and phone mail apps (tables + inline styles).

   Writing rules in the message box:
   - an empty line starts a new paragraph
   - lines starting with "- " become a bullet list
   - **text** becomes bold
   - links (https://…) become clickable
   - {{firstName}}, {{studentName}}, {{lastClassDate}}, {{centerName}} are
     replaced for each person when the email is sent
   ========================================================= */

export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://robomind-edu.com').replace(/\/$/, '');
export const WHATSAPP_NUMBER = String(process.env.REACT_APP_MOBILE_NUMBER1 || '').replace(/\D/g, '');
export const INSTAGRAM_URL = 'https://www.instagram.com/robomind.lb';
const LOGO_URL = `${SITE_URL}/assets/img/logo/logowhitenew.png`;

const C = {
  page: '#F2F5FA',
  card: '#FFFFFF',
  navy: '#0A1230',
  text: '#33405C',
  muted: '#7A869E',
  line: '#E6EAF2',
  yellow: '#FFD21F',
  soft: '#F6F8FC',
};
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export const esc = (t) =>
  String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const whatsappLink = (text = 'Hello! I have a question about RoboMind Academy') =>
  WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}` : SITE_URL;

/* where the big button goes */
export const BUTTONS = {
  whatsapp: { label: 'WhatsApp', url: () => whatsappLink() },
  freetrial: { label: 'Free trial page', url: () => `${SITE_URL}/FreeTrial` },
  website: { label: 'Website', url: () => SITE_URL },
  programs: { label: 'Programs page', url: () => `${SITE_URL}/Programs` },
  custom: { label: 'Other link', url: (custom) => custom || SITE_URL },
  none: { label: 'No button', url: () => '' },
};

/* inline text: escape, **bold**, links */
const inline = (t) =>
  esc(t)
    .replace(/\*\*(.+?)\*\*/g, `<strong style="color:${C.navy};">$1</strong>`)
    .replace(
      /(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g,
      `<a href="$1" style="color:#0B63CE;text-decoration:underline;" target="_blank">$1</a>`
    );

/* the message box text -> email paragraphs and lists */
export function textToHtml(text) {
  const blocks = String(text || '')
    .replace(/\r/g, '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  return blocks
    .map((block) => {
      const lines = block.split('\n');
      if (lines.every((l) => /^\s*[-•]\s+/.test(l))) {
        const items = lines
          .map(
            (l) =>
              `<li dir="auto" style="margin:0 0 6px;padding-left:4px;">${inline(l.replace(/^\s*[-•]\s+/, ''))}</li>`
          )
          .join('');
        return `<ul style="margin:0 0 18px;padding-left:22px;color:${C.text};font-size:16px;line-height:1.6;">${items}</ul>`;
      }
      return `<p dir="auto" style="margin:0 0 18px;color:${C.text};font-size:16px;line-height:1.65;">${lines
        .map(inline)
        .join('<br>')}</p>`;
    })
    .join('\n');
}

/* the full email (one HTML document) */
export function renderEmail({ preheader, heading, message, buttonType, buttonLabel, buttonUrl, audience }) {
  const btn = BUTTONS[buttonType] || BUTTONS.none;
  const href = btn.url(buttonUrl);
  const isCenter = audience === 'centers';
  const reason = isCenter
    ? 'You are receiving this email because we would love to work with your center.'
    : 'You are receiving this email because your child has taken a class with RoboMind Academy.';

  const button =
    href && buttonLabel
      ? `
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 26px;">
              <tr>
                <td align="center" bgcolor="${C.yellow}" style="border-radius:10px;">
                  <a href="${esc(href)}" target="_blank" style="display:inline-block;padding:14px 30px;font-family:${FONT};font-size:16px;font-weight:700;color:${C.navy};text-decoration:none;border-radius:10px;">${esc(buttonLabel)}</a>
                </td>
              </tr>
            </table>`
      : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="x-apple-disable-message-reformatting">
<title>${esc(heading || 'RoboMind Academy')}</title>
<style>
  @media (max-width: 620px) {
    .rm-card { width: 100% !important; border-radius: 0 !important; }
    .rm-pad { padding: 26px 20px !important; }
    .rm-h1 { font-size: 22px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:${C.page};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:${C.page};">${esc(preheader || '')}&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${C.page}" style="background:${C.page};">
  <tr>
    <td align="center" style="padding:28px 12px;">
      <table role="presentation" class="rm-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:${C.card};border-radius:16px;overflow:hidden;">
        <tr>
          <td align="center" bgcolor="${C.navy}" style="background:${C.navy};padding:22px 24px;">
            <a href="${SITE_URL}" target="_blank" style="text-decoration:none;">
              <img src="${LOGO_URL}" alt="RoboMind Academy" height="40" style="height:40px;border:0;display:block;color:#FFFFFF;font-family:${FONT};font-size:20px;font-weight:700;">
            </a>
          </td>
        </tr>
        <tr>
          <td height="4" bgcolor="${C.yellow}" style="background:${C.yellow};font-size:0;line-height:0;">&nbsp;</td>
        </tr>
        <tr>
          <td class="rm-pad" style="padding:36px 40px 18px;font-family:${FONT};">
            ${heading ? `<h1 class="rm-h1" dir="auto" style="margin:0 0 20px;color:${C.navy};font-size:26px;line-height:1.25;font-weight:800;">${inline(heading)}</h1>` : ''}
            ${textToHtml(message)}
            ${button}
            <p style="margin:0 0 6px;color:${C.text};font-size:16px;line-height:1.6;">Warm regards,</p>
            <p style="margin:0 0 10px;color:${C.navy};font-size:16px;font-weight:700;">The RoboMind team</p>
          </td>
        </tr>
        <tr>
          <td style="padding:0 40px 30px;font-family:${FONT};">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-top:1px solid ${C.line};">
              <tr>
                <td align="center" style="padding-top:20px;font-size:14px;">
                  <a href="${whatsappLink()}" target="_blank" style="color:${C.navy};text-decoration:none;font-weight:700;">WhatsApp</a>
                  <span style="color:${C.muted};"> · </span>
                  <a href="${INSTAGRAM_URL}" target="_blank" style="color:${C.navy};text-decoration:none;font-weight:700;">Instagram</a>
                  <span style="color:${C.muted};"> · </span>
                  <a href="${SITE_URL}" target="_blank" style="color:${C.navy};text-decoration:none;font-weight:700;">robomind-edu.com</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <table role="presentation" class="rm-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;">
        <tr>
          <td align="center" style="padding:18px 20px 6px;font-family:${FONT};font-size:12px;line-height:1.6;color:${C.muted};">
            RoboMind Academy · Online coding, robotics &amp; AI for kids · Lebanon<br>
            ${reason}<br>
            Don’t want these emails? Reply with “unsubscribe” and we will remove you.
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

/* merge tags for one person (values are made safe for HTML) */
export const firstNameOf = (full) => String(full || '').trim().split(/\s+/)[0] || '';

export function fillTags(text, values, { html = true } = {}) {
  const safe = (v) => (html ? esc(v) : String(v ?? ''));
  return String(text || '')
    .replace(/\{\{firstName\}\}/g, safe(values.firstName || 'your child'))
    .replace(/\{\{studentName\}\}/g, safe(values.studentName || 'your child'))
    .replace(/\{\{lastClassDate\}\}/g, safe(values.lastClassDate || 'their last class'))
    .replace(/\{\{centerName\}\}/g, safe(values.centerName || 'your center'));
}

/* things to fix before sending: [placeholders] left in the text */
export const leftoverPlaceholders = (...texts) => {
  const found = new Set();
  texts.forEach((t) => (String(t || '').match(/\[[^\]\n]{2,60}\]/g) || []).forEach((m) => found.add(m)));
  return [...found];
};
